/**
 * The variable-font caption line — Channel A's output.
 *
 * Two axes driven by two independent signals:
 *
 *   wght <- aperture   how far the jaw was open
 *   wdth <- width      smile against pucker
 *
 * That independence is the whole point. One value smeared across a line reads
 * as an animation; two orthogonal ones read as a signature of a particular
 * mouth making a particular sound, which is the thing this project claims to
 * be able to show.
 *
 * GRAD is left at 0 until Phase 7, where flatness drives it. It is the right
 * axis for voice quality precisely because it changes apparent weight *without*
 * changing metrics — nothing reflows when it moves, so it is safe to animate on
 * live text in a way `wght` never is.
 *
 * **Finalised words are rendered once and never touched again.**
 * `font-variation-settings` on hundreds of spans is a layout thrash: every
 * change to any of them can invalidate the whole line. Since a committed word
 * is frozen by contract (rule 4 of the architecture section), its span can be
 * built once and left alone, leaving only the interim tail — under ten words in
 * practice — live.
 */

import { config } from '../core/config.js';
import { glyphFor } from './gestureglyph.js';

const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * A CharToken's variation settings.
 *
 * Exported and pure so the mapping can be tested without a DOM: it is the one
 * piece of Phase 3 that carries an actual claim about numbers.
 *
 * @param {import('../core/types.js').CharToken} char
 * @returns {{wght: number, wdth: number, grad: number}}
 */
export function axesFor(char) {
  const r = config.render;
  const grad = char.grad ?? 0;
  return {
    wght: lerp(r.WGHT_MIN, r.WGHT_MAX, clamp01(char.ap ?? 0)),
    wdth: lerp(r.WDTH_MIN, r.WDTH_MAX, clamp01(char.wd ?? 0.5)),
    // `grad` arrives as -1..1 around neutral. GRAD_MIN is already negative, so
    // it is used as the target directly — an earlier version negated it, which
    // sent a negative grad to a *positive* GRAD and made a breathy voice render
    // heavier than a clean one. Invisible until Phase 7, because grad was
    // pinned at 0 and the branch was never taken with a negative value.
    grad: lerp(0, grad >= 0 ? r.GRAD_MAX : r.GRAD_MIN, Math.abs(grad)),
  };
}

/**
 * Voice quality from spectral flatness, as -1..1 around neutral.
 *
 * Flatness is the tone-to-noise measure: a clean voiced vowel sits low, a
 * growled or breathy one sits high. Mapping it to GRAD rather than to weight is
 * the whole reason this axis exists — GRAD changes apparent weight *without*
 * changing metrics, so a word can grow heavier mid-utterance without the line
 * reflowing around it.
 *
 * This is the information ASR throws away outright. A growled "noooo" and a
 * clean one produce identical text; here they produce visibly different text.
 *
 * @param {?number} flatness
 * @returns {number} -1 (clean) .. 1 (noisy), 0 when there is nothing to say
 */
export function gradFor(flatness) {
  if (typeof flatness !== 'number' || !Number.isFinite(flatness) || flatness <= 0) return 0;
  const { GRAD_FLATNESS_LO: lo, GRAD_FLATNESS_HI: hi } = config.render;
  return Math.max(-1, Math.min(1, ((flatness - lo) / (hi - lo)) * 2 - 1));
}

/** The CSS value, rounded — sub-unit precision on these axes is invisible. */
export function variationSettings(char) {
  const { wght, wdth, grad } = axesFor(char);
  return `'wght' ${Math.round(wght)}, 'wdth' ${Math.round(wdth)}, 'GRAD' ${Math.round(grad)}`;
}

/**
 * One character, carrying the mouth that made it.
 *
 * The smallest thing this project draws, and the only place a CharToken becomes
 * a glyph. Both callers want exactly this — the line builds a word out of them,
 * and the emitter launches them one at a time — and building it twice would be
 * two places for the mapping to drift.
 *
 * @param {import('../core/types.js').CharToken} char
 * @returns {HTMLElement}
 */
export function charElement(char) {
  const span = document.createElement('span');
  span.className = 'cap-char';
  span.textContent = char.ch;
  span.style.fontVariationSettings = variationSettings(char);
  return span;
}

/**
 * One word as a span of per-glyph spans.
 *
 * Standalone because the emitter needs exactly this and nothing else around it:
 * a word it can launch out of a mouth carries the same axes as a word sitting in
 * a line.
 *
 * @param {import('../core/types.js').Word} word
 * @param {boolean} [isFinal]
 * @returns {HTMLElement}
 */
export function wordElement(word, isFinal = true) {
  const el = document.createElement('span');
  el.className = `cap-word${isFinal ? ' final' : ''}`;

  for (const char of word.chars) el.append(charElement(char));
  return el;
}

export class Caption {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;

    /** Frozen words, appended once each. */
    this.committedEl = document.createElement('span');
    this.committedEl.className = 'cap-committed';

    /** The live tail, rebuilt on every update. */
    this.interimEl = document.createElement('span');
    this.interimEl.className = 'cap-interim';

    root.replaceChildren(this.committedEl, this.interimEl);

    /** How many committed words are already in the DOM. */
    this._committedRendered = 0;

    /**
     * Spans of every gesture placed on the line, for the collision test below.
     * @type {{from: number, to: number}[]}
     */
    this._gestures = [];
  }

  /**
   * Both channels described the same moment. Say so rather than pick a winner.
   *
   * This happens for real and it is not an edge case. On a recorded take the
   * speaker said "pow"; ASR reported the word "pull" while Channel B measured a
   * plosive at the same instant, so the line carried both a word and a burst for
   * one sound. SPEC1.md does not address it at all — it assumes Channel B
   * handles what ASR discards, and mostly that holds, but a plosive is exactly
   * the sound both channels can hear.
   *
   * Three things could be done and only one of them is honest. Suppressing the
   * word asserts Channel B is right, which it demonstrably is not always.
   * Showing both plainly makes the line stutter and says nothing. Fading the
   * contested word keeps every piece of information on the page and marks the
   * one that is in doubt — the same thing the gesture glyphs already do with
   * their own confidence, and the same reason GRAD was chosen for voice quality:
   * **opacity carries the doubt without touching the metrics**, so nothing
   * reflows and no finalised glyph changes shape.
   *
   * That last point matters against rule 4. A committed word is frozen, and it
   * stays frozen: this changes what it looks like, never what it says or how
   * much room it takes.
   */
  _markCollisions(from, to) {
    // Walk back from the newest. Everything is in spoken order, so the first
    // element that ends before this span begins ends the search — a collision is
    // bounded by LAG, not by the length of the line.
    let el = this.committedEl.lastElementChild;
    while (el) {
      if (el._to !== undefined && el._to < from) break;
      if (el.classList.contains('cap-word') && el._from < to && el._to > from) {
        el.classList.add('contested');
      }
      el = el.previousElementSibling;
    }
  }

  /**
   * @param {import('../core/types.js').Word} word
   * @param {boolean} isFinal
   */
  _buildWord(word, isFinal) {
    const el = wordElement(word, isFinal);

    // A real space between words, so the line breaks and copies normally.
    el.append(document.createTextNode(' '));
    el._t = word.tBind;
    // The span the word claims, which is what a gesture is tested against.
    el._from = word.tBind;
    el._to = word.tBind + (word.dur ?? 0);

    // A word can arrive after the gesture it collides with — ASR is 300 ms late
    // and a plosive 15 — so the test runs from both directions.
    for (const g of this._gestures) {
      if (el._from < g.to && el._to > g.from) {
        el.classList.add('contested');
        break;
      }
    }
    return el;
  }

  /**
   * Insert an element at its place in spoken time.
   *
   * Not an append, because the two channels do not arrive in the order they
   * happened. A gesture is released `LAG` after its onset; a transcript word is
   * appended when the recogniser finalises it, which can be two seconds after
   * it was spoken. Appending each as it arrives would put a burst before the
   * word that preceded it — the exact failure `Timeline` exists to prevent,
   * reintroduced one layer higher up.
   *
   * The walk is backwards and almost always stops immediately: things do mostly
   * arrive in order, and the cost is proportional to how far out of order this
   * one was rather than to the length of the line.
   */
  _insertOrdered(el, t) {
    el._t = t;
    let after = this.committedEl.lastElementChild;
    while (after && after._t > t) after = after.previousElementSibling;
    if (after) after.after(el);
    else this.committedEl.prepend(el);
  }

  /**
   * Place a gesture glyph on the line at the moment it was made.
   *
   * @param {import('../core/types.js').GestureEvent} event
   * @returns {?SVGElement}
   */
  addGesture(event) {
    const glyph = glyphFor(event);
    if (!glyph) return null;

    // Wrapped, so the glyph sits in the text flow with real spaces around it
    // and the line still breaks and copies as prose.
    const wrap = document.createElement('span');
    wrap.className = 'cap-gesture';
    wrap.append(glyph, document.createTextNode(' '));

    const from = event.t;
    const to = event.t + (event.dur ?? 0);
    wrap._from = from;
    wrap._to = to;
    this._insertOrdered(wrap, event.t);

    // Kept even once placed: a word that collides with this may not have been
    // reported yet.
    this._gestures.push({ from, to });
    if (this._gestures.length > 256) this._gestures.shift();
    this._markCollisions(from, to);
    return glyph;
  }

  /**
   * Bring the DOM up to date with the transcript.
   *
   * Cheap by construction: committed words are appended once as they arrive and
   * never revisited, so the per-frame cost is proportional to the interim tail
   * rather than to everything ever said.
   *
   * @param {{committed: import('../core/types.js').Word[], interim: import('../core/types.js').Word[]}} transcript
   */
  render(transcript) {
    const { committed, interim } = transcript;

    // The transcript was reset out from under us.
    if (committed.length < this._committedRendered) {
      this.committedEl.replaceChildren();
      this._committedRendered = 0;
    }

    if (committed.length > this._committedRendered) {
      for (let i = this._committedRendered; i < committed.length; i++) {
        const word = committed[i];
        this._insertOrdered(this._buildWord(word, true), word.tBind);
      }
      this._committedRendered = committed.length;
    }

    const frag = document.createDocumentFragment();
    for (const word of interim) frag.append(this._buildWord(word, false));
    this.interimEl.replaceChildren(frag);
  }

  clear() {
    this.committedEl.replaceChildren();
    this.interimEl.replaceChildren();
    this._committedRendered = 0;
    this._gestures = [];
  }

  /** Every rendered character span, in reading order. For the SVG export. */
  chars() {
    return [...this.root.querySelectorAll('.cap-char')];
  }

  /** Word elements, in reading order. */
  wordElements() {
    return [...this.root.querySelectorAll('.cap-word')];
  }
}

export default Caption;
